import { useState, useEffect, useRef } from 'react';
import Close from '@mui/icons-material/Close';
import { BoxComponent } from '../../base/box/Box';
import { DialogComponent } from '../../base/dialog/DialogComponent';
import { styles } from './AnnouncementPopup.styles';
import { Button, IconButton, Typography } from '@mui/material';
import { AnnouncementPopupProps } from './AnnouncementPopup.types';
import { ICampaignDataResponse, WebsiteContent } from '@zeus/shared';
import { apiClient, announcementPopupInitialProps } from '../Commons';

// Create a singleton flag outside the component
let modalInitialized = false;

const AnnouncementPopup = ({
  announcementData = announcementPopupInitialProps.announcementData,
  environmentUrl = announcementPopupInitialProps.environmentUrl,
  businessId = announcementPopupInitialProps.businessId,
  restaurantId = announcementPopupInitialProps.restaurantId,
  viewMode = announcementPopupInitialProps.viewMode,
  builderMode = announcementPopupInitialProps.builderMode,
  previewMode = announcementPopupInitialProps.previewMode,
  handlePopupClose = announcementPopupInitialProps.handlePopupClose,
  publishedMode = false,
  globalStyles = announcementPopupInitialProps.globalStyles,
  isDesktop = true,
}: AnnouncementPopupProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [announcement, setAnnouncement] = useState<ICampaignDataResponse | null>(null);
  const initializedRef = useRef(false);
  const content = announcement?.content[0].details as WebsiteContent;

  useEffect(() => {
    // Skip if already initialized or not in browser
    if (
      (builderMode && !previewMode) ||
      (!publishedMode && !viewMode && !builderMode && !previewMode) ||
      initializedRef.current ||
      typeof window === 'undefined'
    )
      return;
    initializedRef.current = true;

    // Global check to prevent duplicate initialization
    if (modalInitialized) return;
    modalInitialized = true;

    const getAnnouncementWithDelay = (businessId: string, restaurantId: string) => {
      setTimeout(async () => {
        try {
          const client = apiClient(environmentUrl);
          const result: any = await client.get(`active-announcement/?restaurantId=${restaurantId}`, {
            headers: {
              'x-tenant-id': businessId,
            },
          });
          const data = result?.response?.data;
          if (!data || new Date() <= new Date(data.campaign.schedule) || new Date() >= new Date(data.campaign.endDate))
            return;
          setAnnouncement(data.campaign);
          setIsOpen(true);
        } catch (e) {
          console.log(e);
        }
      }, 13000);
    };

    if (viewMode) {
      setAnnouncement(announcementData);
      setIsOpen(true);
    } else if (builderMode && previewMode) {
      getAnnouncementWithDelay(businessId, restaurantId);
    } else if (!builderMode) {
      getAnnouncementWithDelay(businessId, restaurantId);
    }
  }, [builderMode, previewMode, viewMode]);

  const handleClose = () => {
    setIsOpen(false);
    handlePopupClose?.();
  };

  return (
    <DialogComponent open={isOpen} maxWidth="lg" fullWidth sx={{ overflow: 'hidden' }}>
      <IconButton sx={styles.icon} onClick={handleClose}>
        <Close />
      </IconButton>
      <BoxComponent sx={styles.contentContainer}>
        <BoxComponent sx={styles.textContainer}>
          <BoxComponent sx={styles.textContent}>
            <Typography
              sx={{
                ...styles.title,
                ...(globalStyles?.heading?.fontFamily && { fontFamily: globalStyles.heading.fontFamily }),
              }}
            >
              {content?.headline}
            </Typography>
            <Typography
              sx={{
                ...styles.type,
                ...(globalStyles?.subHeading?.fontFamily && { fontFamily: globalStyles.subHeading.fontFamily }),
                ...(!content?.body && { WebkitLineClamp: isDesktop ? 5 : 11 }),
              }}
            >
              {content?.subText}
            </Typography>
            <Typography
              sx={{
                ...styles.description,
                ...(globalStyles?.bodyText?.fontFamily && { fontFamily: globalStyles.bodyText.fontFamily }),
                ...(!content?.subText && { WebkitLineClamp: isDesktop ? 5 : 11 }),
              }}
            >
              {content?.body}
            </Typography>
          </BoxComponent>
          {content?.action ? (
            <Button
              variant="contained"
              target={'_blank'}
              href={content.action.url}
              sx={{
                ...styles.button,
                ...(globalStyles?.bodyText?.fontFamily && { fontFamily: globalStyles.bodyText.fontFamily }),
              }}
              aria-label={content.action.text}
            >
              {content.action.text}
            </Button>
          ) : undefined}
        </BoxComponent>
        <BoxComponent sx={styles.imageContainer}>
          <img
            src={content?.media?.url || ''}
            loading="lazy"
            alt="announcement"
            aria-label="announcement"
            onContextMenu={(e) => e.preventDefault()}
          />
        </BoxComponent>
      </BoxComponent>
    </DialogComponent>
  );
};

export default AnnouncementPopup;
